import axios from 'axios';
import { createMockStudioApi, mockScenario } from './studioApi.mock';

// REST client for core-platform `/api/studio` — ApiDog project 988795, folder
// 11052253, contract 2.0.0 (1.7.0 conversational runs; 1.8.0 plans, credit packs and
// Stripe embedded checkouts: folder "11 — Plans & Payments", `features.payments`; 2.0.0
// every Instagram profile a separate workspace: the creator is the account, `profiles` its
// workspaces, and projects, memories, topics and prompt audio take a `profile_id`; 2.3.0
// Personality, My Voice of 2.2.0 renamed: folder "18 — Personality", behind
// `features.personality`, its routes 403 `personality_unavailable` while core keeps
// STUDIO_PERSONALITY_ENABLED off; 2.4.0 the refine suggestions the AI writes with each
// script, `suggestions` on the revision; 2.5.0 what works for the creator, the best and
// worst reels by reach compared in `personality.performance`, and the `comparing` stage;
// 2.6.0 the ad attribution, `attribution` on POST /creator and POST /onboarding; 2.7.0
// arts, single post and carousel: folder "19 — Arts", the formats listed in
// `capabilities.formats`; 2.8.0 personal templates, imported from a file or saved from an
// art, renamed and deleted, behind `features.art_personal_templates`; 2.9.0 images in the arts:
// the gallery's `origin` and `description` (filters and the creator's description), the
// community gallery behind `features.community_images`, and the image question of an art run
// (`kind: images`, answered `choose_images` or `skip`) whose AI generation is an `image` quote,
// behind `features.art_image_generation`); 2.13.0 videos in carousel pages: `video` layers,
// the profile's `art_video` uploads and `mp4` exports, wherever the carousel is offered.
// The pin is core-platform specs/studio-reels-2.13.0.openapi.json, mirrored in
// data/studio.openapi.json (scripts/studio-art-videos-contract.js wrote it from 2.12.0).
// Resources come back unwrapped (no `data` envelope); failures are the contract
// `StudioError`, rethrown as an Error with `code`, `status`, `details` and
// `retryable`. axios carries the base URL and Bearer token configured by AuthContext.
export const STUDIO_CONTRACT_VERSION = '2.13.0';

// `Idempotency-Key` must match /^[A-Za-z0-9._:-]{16,128}$/. Reuse a key only to
// retry the same request; a new operation needs a new key.
export const idempotencyKey = (prefix = 'studio') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
// One key per request content (`prefix` within the key's alphabet): sent again after its
// response was lost, the same request replays what core committed (core keeps 2xx answers
// only, so a corrected or refused request runs again), and a different one gets its own key.
export function requestKey(prefix, value) {
  const text = JSON.stringify(value);
  const hash = seed => {
    let h = seed;
    for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h.toString(16).padStart(8, '0');
  };
  return `${prefix}-${hash(0x811c9dc5)}${hash(0x2f6fde01)}`;
}

export function toStudioError(failure) {
  if (failure?.studio) return failure;
  const response = failure?.response;
  const body = response?.data && typeof response.data === 'object' ? response.data : {};
  const error = new Error(body.message || failure?.message || 'Studio request failed');
  error.studio = true;
  error.status = response?.status ?? null;
  error.code = body.code || (!response ? 'network' : response.status === 401 ? 'unauthenticated' : response.status === 429 ? 'too_many_requests' : 'generic');
  error.details = body.details || {};
  error.errors = body.errors || {};
  error.retryable = Boolean(body.retryable);
  return error;
}

// Backend error codes → `studio:error_<key>` copy. Unknown codes fall back to generic.
const ERROR_KEYS = {
  unauthenticated: 'auth',
  not_found: 'missing',
  creator_not_initialized: 'missing',
  studio_unavailable: 'unavailable',
  trending_topics_unavailable: 'topic',
  topic_not_eligible: 'topic',
  project_archived: 'archived',
  insufficient_credits: 'credits',
  memory_disabled: 'memoryDisabled',
  validation_error: 'validation',
  network: 'network',
  too_many_requests: 'busy',
  rate_limited: 'busy',
  service_unavailable: 'busy',
  platform_unavailable: 'busy',
  onboarding_incomplete: 'onboardingIncomplete',
  idempotency_in_progress: 'busy',
  asset_integrity_failed: 'upload',
  asset_not_uploaded: 'upload',
  upload_failed: 'upload',
  transcription_failed: 'transcription',
  transcription_not_reviewed: 'transcription',
  invalid_transcription_transition: 'transcription',
  export_persist: 'exportPersist',
  payments_unavailable: 'payments',
  checkout_not_found: 'payments',
  currency_not_supported: 'currency',
  subscription_exists: 'subscriptionExists',
  subscription_not_found: 'subscriptionChanged',
  subscription_not_active: 'subscriptionInactive',
  plan_change_not_allowed: 'subscriptionChanged',
  // 1.9.0: the creator's own @ set to one of its connected profiles, or one profile too many.
  profile_already_connected: 'profileConnected',
  profile_limit_reached: 'profileLimit',
  // 2.0.0: a project from before the profiles, of an account that has not connected one.
  profile_required: 'profileRequired',
  // 2.3.0 (Personality): the feature is off, a chosen video stopped being selectable, a
  // mapping is already running for the profile, or its @ is no longer a public professional account.
  personality_unavailable: 'personalityUnavailable',
  video_not_selectable: 'videoNotSelectable',
  personality_mapping_in_progress: 'personalityInProgress',
  social_account_not_found: 'socialAccountMissing',
  // Local: the checkout expired or its payment failed (`fulfillment: failed`).
  payment_failed: 'paymentFailed',
  // Local (2.9.0): the image question closed before this answer (answered elsewhere, expired
  // and skipped by core, the run cancelled); the run read again goes on without it.
  images_question_closed: 'imagesClosed',
};
const CONFLICTS = /conflict|active_run_exists|action_not_open|onboarding_not_open|invalid_.*_transition|usage_quote_|usage_operation_settled|idempotency_key_reused|memory_not_reusable|creator_already_exists/;
export const errorKey = error => {
  const code = typeof error === 'string' ? error : error?.code;
  if (!code) return 'generic';
  if (ERROR_KEYS[code]) return ERROR_KEYS[code];
  return CONFLICTS.test(code) ? 'conflict' : 'generic';
};

const path = (...parts) => parts.map(encodeURIComponent).join('/');
// Optional filters left empty are not sent (`q=` would be a 422 on a 1–100 character search).
const query = params => Object.fromEntries(Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== ''));

async function request(method, url, { data, params, key } = {}) {
  const headers = method === 'get' ? {} : { 'Idempotency-Key': key || idempotencyKey() };
  try {
    const response = await axios.request({ method, url: `studio/${url}`, data, params, headers });
    return response.status === 204 ? null : response.data;
  } catch (failure) {
    const error = toStudioError(failure);
    // Same session policy as helpers/api: an expired token logs the user out.
    if (error.status === 401 && window.signed) window.logout?.();
    throw error;
  }
}

// The bytes go to the presigned URL with `fetch`, not axios: the global
// Authorization header must never reach S3 (it breaks the signature).
const uploadFailed = () => Object.assign(new Error('Studio upload failed'), { studio: true, code: 'upload_failed', details: {} });
// 2.13.0: a long upload (a video) reports how far it got, `onProgress(0…1)`, which only an
// XMLHttpRequest can tell.
function putWithProgress(upload, blob, mimeType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(upload.method || 'PUT', upload.url);
    Object.entries({ 'Content-Type': mimeType, ...(upload.headers || {}) }).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = event => { if (event.lengthComputable) onProgress(event.loaded / event.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve(null) : reject(uploadFailed()));
    xhr.onerror = () => reject(uploadFailed());
    xhr.send(blob);
  });
}
async function putUpload(upload, blob, mimeType, { onProgress } = {}) {
  if (onProgress && typeof XMLHttpRequest !== 'undefined') return putWithProgress(upload, blob, mimeType, onProgress);
  let response;
  try {
    response = await fetch(upload.url, { method: upload.method || 'PUT', headers: { 'Content-Type': mimeType, ...(upload.headers || {}) }, body: blob });
  } catch { throw uploadFailed(); }
  if (!response.ok) throw uploadFailed();
  return null;
}

const get = (url, params) => request('get', url, { params });
const post = (url, data, key) => request('post', url, { data, key });
const patch = (url, data, key) => request('patch', url, { data, key });
const del = (url, key) => request('delete', url, { key });

const httpApi = {
  capabilities: () => get('capabilities'),

  // 01 — Creator profile and guided onboarding (`features.social_onboarding`)
  creator: () => get('creator'),
  createCreator: (body, key) => post('creator', body, key),
  updateCreator: body => patch('creator', body),
  // 2.0.0: each profile's workspace settings (interests, audience, voice, locale, memory).
  updateProfile: (id, body) => patch(path('profiles', id), body),
  onboarding: () => get('onboarding'),
  startOnboarding: (body, key) => post('onboarding', body, key),
  answerOnboarding: (body, key) => post('onboarding/answers', body, key),
  completeOnboarding: (body, key) => patch('onboarding', body, key),

  // 02 — Topics (read-only; refreshed by the scheduler), ranked for one profile (`profile_id`)
  topics: params => get('topics', params),
  topic: (id, params) => get(path('topics', id), params),

  // 03 — Projects & chat. The chat is its project: `project.chat_id` is the
  // project id encoded as `stc-`.
  projects: params => get('projects', params),
  project: id => get(path('projects', id)),
  createProject: (body, key) => post('projects', body, key),
  updateProject: (id, body) => patch(path('projects', id), body),
  messages: (chatId, params) => get(`${path('chats', chatId)}/messages`, params),
  createMessage: (chatId, body, key) => post(`${path('chats', chatId)}/messages`, body, key),

  // 04 — Runs
  startRun: (projectId, body, key) => post(`${path('projects', projectId)}/runs`, body, key),
  run: id => get(path('runs', id)),
  updateRun: (id, body) => patch(path('runs', id), body),
  respond: (runId, actionId, body, key) => post(`${path('runs', runId)}/${path('actions', actionId)}/responses`, body, key),

  // 05 — Script revisions
  revisions: (projectId, params) => get(`${path('projects', projectId)}/revisions`, params),
  revision: id => get(path('revisions', id)),
  saveRevision: (projectId, body, key) => post(`${path('projects', projectId)}/revisions`, body, key),

  // 06 — Creator memory
  memories: params => get('memories', params),
  createMemory: (body, key) => post('memories', body, key),
  updateMemory: (id, body) => patch(path('memories', id), body),
  deleteMemory: id => del(path('memories', id)),

  // 07 — Assets & transcriptions. Audio without `project_id` is a prompt recorded on
  // the home composer: it stays in the creator scope until `createProject`
  // adopts its reviewed transcription (`transcription_id`), or is deleted after 24 h.
  prepareAsset: (body, key) => post('assets', body, key),
  putUpload,
  confirmAsset: (id, body) => patch(path('assets', id), body),
  startTranscription: (projectId, body, key) => post(`${path('projects', projectId)}/transcriptions`, body, key),
  startAudioTranscription: (body, key) => post('transcriptions', body, key),
  transcription: id => get(path('transcriptions', id)),
  updateTranscription: (id, body) => patch(path('transcriptions', id), body),

  // 08 — Exports (an art export names `art_revision_id`, PNG/JPEG and its scale)
  createExport: (projectId, body, key) => post(`${path('projects', projectId)}/exports`, body, key),
  updateExport: (id, body) => patch(path('exports', id), body),

  // 19 — Arts (contract 2.7.0, ARTS_CONTRACT.md): offered
  // when `capabilities.formats` lists an art format. Generation and chat edits are the
  // shared runs; these routes are the template gallery, the art revisions (autosave), the
  // profile's brand kit and its image gallery (`art_image` assets).
  artTemplateCategories: params => get('art-template-categories', params),
  artTemplates: params => get('art-templates', params),
  artTemplate: id => get(path('art-templates', id)),
  // 2.8.0, when `features.art_personal_templates` is on: the creator's personal templates, for
  // the whole account — imported from a JSON file or saved from an art, renamed, deleted.
  createArtTemplate: (body, key) => post('art-templates', body, key),
  updateArtTemplate: (id, body, key) => patch(path('art-templates', id), body, key),
  deleteArtTemplate: (id, key) => del(path('art-templates', id), key),
  artRevisions: (projectId, params) => get(`${path('projects', projectId)}/art-revisions`, params),
  artRevision: id => get(path('art-revisions', id)),
  // The creator's edits update the current version in place (only AI runs make versions).
  updateArtRevision: (id, body, key) => patch(path('art-revisions', id), body, key),
  brandKit: profileId => get(`${path('profiles', profileId)}/brand-kit`),
  updateBrandKit: (profileId, body, key) => patch(`${path('profiles', profileId)}/brand-kit`, body, key),
  // 2.9.0: `origin` (`upload` | `generated`) and `q` (a piece of the description) filter the gallery.
  assets: params => get('assets', query(params)),
  // 2.9.0: the creator's words for what a gallery image shows, `{ description }` (1–500).
  describeAsset: (id, body, key) => patch(path('assets', id), body, key),
  // 2.9.0, `features.community_images` (else 403 `studio_unavailable`): the curated gallery.
  communityImageCategories: () => get('community-image-categories'),
  communityImages: params => get('community-images', query(params)),

  // 10 — Usage & credits. 2.9.0: `{ kind: image, action_id, slot_ids }` prices the places of an
  // image question to generate with AI; every quote carries `action_id` (null but on `image`).
  quote: (projectId, body, key) => post(`${path('projects', projectId)}/usage-quotes`, body, key),
  audioQuote: (body, key) => post('usage-quotes', body, key),
  // The same creator-scope route, `kind: personality` (2.3.0): `profile_id` + the 3 `video_ids`.
  personalityQuote: (body, key) => post('usage-quotes', body, key),
  usage: () => get('usage'),

  // 18 — Personality (2.3.0, `features.personality`): the profile's Reels (cursor bound to the
  // profile, a batch may hold fewer than `limit`: photos and carousels are left out), its
  // personality (the active one and the latest mapping) and a new mapping from
  // `limits.personality_videos` of them, polled until it settles.
  profileVideos: (profileId, params) => get(`${path('profiles', profileId)}/videos`, params),
  profilePersonality: profileId => get(`${path('profiles', profileId)}/personality`),
  startPersonalityMapping: (profileId, body, key) => post(`${path('profiles', profileId)}/personality-mappings`, body, key),
  personalityMapping: id => get(path('personality-mappings', id)),

  // 11 — Plans & payments. Credits and plan changes are granted by the Stripe webhook:
  // a checkout is polled until its `fulfillment` settles. An upgrade charges the card
  // on file at once (`pending_change` when 3DS or a decline holds it).
  plans: () => get('plans'),
  subscription: () => get('subscription'),
  // What an upgrade charges now; `proration_date` goes back with the PATCH to pin it.
  subscriptionPreview: plan => get('subscription/preview', { plan }),
  updateSubscription: (body, key) => patch('subscription', body, key),
  createCheckout: (body, key) => post('checkouts', body, key),
  checkout: id => get(path('checkouts', id)),
  invoices: params => get('invoices', params),
  createPortalSession: key => post('payment-portal-sessions', undefined, key),

  // 20 — Direct Messages (contract 2.14.0, status designing, data/automations.openapi.json): core
  // has no route yet, so only `?mock=true` opens these screens (enums/automations.js).
  // 01: the channels and the official connection of a profile's account.
  platforms: params => get('platforms', query(params)),
  connections: params => get('connections', query(params)),
  createConnection: (body, key) => post('connections', body, key),
  connection: id => get(path('connections', id)),
  disconnect: (id, key) => del(path('connections', id), key),
  // 02: the home and the templates.
  automationsOverview: params => get('automations/overview', params),
  automationTemplates: params => get('automations/templates', query(params)),
  // 03: the automations (`trashed` lists the trash) and the media their trigger can aim at.
  automations: params => get('automations', query(params)),
  createAutomation: (body, key) => post('automations', body, key),
  automation: id => get(path('automations', id)),
  updateAutomation: (id, body, key) => patch(path('automations', id), body, key),
  deleteAutomation: (id, key) => del(path('automations', id), key),
  restoreAutomation: (id, key) => post(`${path('automations', id)}/restore`, undefined, key),
  media: params => get('media', query(params)),
  mediaItem: id => get(path('media', id)),
  // 04: results.
  automationInsights: id => get(`${path('automations', id)}/insights`),
  automationExecutions: (id, params) => get(`${path('automations', id)}/executions`, query(params)),
  // 05: the Direct.
  conversations: params => get('conversations', query(params)),
  conversation: id => get(path('conversations', id)),
  updateConversation: (id, body, key) => patch(path('conversations', id), body, key),
  directMessages: (id, params) => get(`${path('conversations', id)}/messages`, query(params)),
  sendDirectMessage: (id, body, key) => post(`${path('conversations', id)}/messages`, body, key),
};

// `?mock=true` (see studioApi.mock.js): routes the double does not implement
// answer like an unavailable dependency instead of reaching the network.
const unsupported = () => Promise.reject(toStudioError({ response: { status: 503, data: { code: 'service_unavailable', message: 'Not available in the Studio mock.' } } }));
const scenario = mockScenario();
export const studioMock = scenario ? createMockStudioApi(scenario) : null;
export const studioApi = studioMock
  ? { ...Object.fromEntries(Object.keys(httpApi).map(name => [name, unsupported])), ...studioMock.api }
  : httpApi;
